/* SeatFit selection rules, loaded last of the core files (snippets/seatfit-config.liquid): the shopper's picks
 * (kept in the URL, seatfit-core-url-params.js) + a resolved vehicle -> steps to show, the one fitment row meant
 * and the cart line properties. Seat sets and seat types live here; covers and mats finish in
 * seatfit-core-cover-mat-rules.js. */
(function () {
  'use strict';
  var SF = window.SeatFit;
  if (!SF) { console.warn('[SeatFit] seatfit-core.js must load before seatfit-core-rules.js'); return; }
  if (SF.stepsFor) return; // second copy of the tag

  var slugify = SF.slugify, seatSlugs = SF.seatSlugs;
  var STEP_KEYS = ['body', 's2', 's3']; // URL param holding the value of step 1, 2, 3
  // Seat rows whose subtype each set asks (Full Set = front + 2nd row); a row asks only when the data lists choices.
  var SUB_ROWS = { front: ['front'], full: ['front', 'rear'], rear: ['rear'], third: ['third'] };
  // Step name -> hidden cart field. Classics name a step "Submodel" (AC Shelby Cobra) while "Model" owns _submodel.
  var STEP_PROPS = { body: '_body', cab: '_cab', 'seat-count': '_seat_count', model: '_submodel',
    submodel: '_trim' };

  function stepKey(i) { return STEP_KEYS[i] || 's' + (i + 1); }
  function genLabel(r) { return r.d ? r.g + ' ' + r.d : r.g; }

  // Distinct values (by slug) in the reference buy box order: step 1 follows its fixed body list (Sedan, Coupe,
  // Convertible ... Wagon), unlisted values after it A-Z; later steps A-Z. First-seen order followed our newest-first
  // generations instead (E-Class 2020 read Convertible, Coupe, Sedan, Wagon; Ram 2020 Crew, Quad, Mega, Regular Cab).
  var BODY_RANK = ['base', 'sedan', 'sedan w/ high wing', 'sedan w/o high wing', 'coupe', 'gran coupe', 'gran sedan',
    'convertible', 'cabriolet', 'w/ high wing', 'w/ high wing spoiler', 'w/ low profile spoiler', 'w/ spoiler',
    'w/ wing', 'w/o high wing spoiler', 'w/o spoiler', 'w/o wing', 'with rear spoiler', 'without rear spoiler',
    'hybrid', 'hatchback', 'kammback', 'koup', 'compact', 'crosstour', 'gt', 'wagon', 'estate wagon'];
  function uniq(values, stepIndex) {
    var seen = {};
    var rank = function (v) { var r = stepIndex === 0 ? BODY_RANK.indexOf(String(v).trim().toLowerCase()) : -1;
      return r < 0 ? BODY_RANK.length : r; };
    return values.filter(function (v) {
      var s = slugify(v);
      return v && !seen[s] ? (seen[s] = true) : false;
    }).sort(function (a, b) { return rank(a) - rank(b) || String(a).localeCompare(String(b)); });
  }

  // A single option is selected for the shopper (still shown, like Santa Fe "5 passenger").
  function pickOption(options, wantSlug) {
    if (options.length === 1) return options[0];
    if (!wantSlug) return null;
    for (var i = 0; i < options.length; i++) if (slugify(options[i]) === wantSlug) return options[i];
    return null;
  }

  // picks uses the URL param names (body, s2-s12, gen, set, seat, color), so readParams() fits as-is. Steps come back up
  // to the first unanswered one; `missing` names what blocks the buy button: {kind:'vehicle'|'step'|'gen'|'set'|'seat'|'color'}.
  function stepsFor(ctx, picks) {
    picks = picks || {};
    var out = { steps: [], needGen: false, genOptions: [], gen: null, row: null,
      sets: [], set: null, subtypes: [], complete: false, missing: null };
    if (!ctx || ctx.status !== 'ok' || !ctx.rows || !ctx.rows.length) {
      out.missing = { kind: 'vehicle', name: 'Vehicle' };
      return out;
    }
    var rows = ctx.rows;
    // Seats read step i at position i of every row. Covers and mats go by step name in SF.stepOrder(): their rows
    // may name step i differently (Silverado 2024 mats: the EV row starts with Floor Mat Set, the rest with Cab).
    var names = ctx.kind ? SF.stepOrder(rows) : null;
    var at = function (r, i) {
      return names ? (r.st || []).filter(function (p) { return p && slugify(p[0]) === names[i]; })[0] : r.st && r.st[i];
    };
    var depth = names ? names.length : rows.reduce(function (n, r) { return Math.max(n, (r.st || []).length); }, 0);
    for (var i = 0; i < depth; i++) {
      var has = rows.filter(function (r) { return at(r, i); });
      if (!has.length) continue;
      var options = uniq(has.map(function (r) { return at(r, i)[1]; }), i);
      // By name, a lone value that only some rows have filters nothing and would read as asked of every vehicle
      // (Camaro covers: "Submodel ZL1" next to the LS, LT, RS, SS trims), so it is not shown.
      if (names && options.length === 1 && has.length < rows.length) continue;
      var key = stepKey(i);
      var step = { name: at(has[0], i)[0], key: key, options: options,
        value: pickOption(options, slugify(picks[key])), auto: options.length === 1 };
      out.steps.push(step);
      if (step.value == null) {
        out.missing = { kind: 'step', name: step.name, key: key };
        return out;
      }
      var chosen = slugify(step.value);
      // A row without this step fits whatever is picked here (Silverado EV has no Cab, the gas ones do); dropping it
      // would make that vehicle unbuyable, so it stays in play and the Generation step below tells it apart.
      rows = rows.filter(function (r) { return !at(r, i) || slugify(at(r, i)[1]) === chosen; });
    }
    if (rows.length > 1) {
      // Same step values in overlapping generations (changeover year): let the shopper say which.
      out.needGen = true;
      out.genOptions = rows.map(function (r) {
        var label = genLabel(r);
        // sub + sets let the step say how the options differ (seatfit-generation-hints.js); covers/mats have no sub.
        return { g: r.g, d: r.d, k: r.k, label: label, value: slugify(label), sub: r.sub, sets: r.sets };
      });
      // `gen` carries the option `value` (label slug): two generations can share years and differ only by detail
      // (Silverado 2015-2019 2500HD vs 3500HD). A bare year range still works when it names exactly one row.
      var wantGen = slugify(picks.gen);
      var byLabel = rows.filter(function (r) { return wantGen && slugify(genLabel(r)) === wantGen; });
      var byYears = rows.filter(function (r) { return wantGen && slugify(r.g) === wantGen; });
      rows = byLabel.length ? byLabel : (byYears.length === 1 ? byYears : []);
      if (!rows.length) {
        out.missing = { kind: 'gen', name: 'Generation', key: 'gen' };
        return out;
      }
      out.gen = rows[0].g;
    }
    var row = out.row = rows[0];
    // Covers and mats sell no seat sets: steps + generation found the row, their own rules finish it.
    if (ctx.kind) return SF.finishKindSelection(out, ctx, picks);
    out.sets = (row.sets || []).slice();
    var wantSet = slugify(picks.set);
    out.set = out.sets.length === 1 ? out.sets[0] : (out.sets.indexOf(wantSet) >= 0 ? wantSet : null);
    if (!out.set) {
      out.missing = { kind: 'set', name: 'Seat Set', key: 'set' };
      return out;
    }
    addSubtypes(out, row, seatSlugs(picks.seat).split('_'));
    out.complete = !out.missing;
    return out;
  }

  // All asked seat rows show at once (Full Set: front + rear). Slot i of `seat` answers asked row i; a slug in the wrong
  // slot may answer another open row, never two: Ram 1500 offers "40/20/40 Split Bench" front AND rear.
  function addSubtypes(out, row, parts) {
    var asked = SUB_ROWS[out.set].filter(function (pos) { return row.sub && row.sub[pos] && row.sub[pos].length; });
    var names = asked.map(function (pos) { return row.sub[pos].map(function (o) { return o.n; }); });
    var used = [];
    var values = names.map(function (list, i) {
      var v = pickOption(list, parts[i]);
      if (v != null && slugify(v) === parts[i]) used[i] = true;
      return v;
    });
    asked.forEach(function (pos, i) {
      for (var k = 0; values[i] == null && k < parts.length; k++) {
        if (!used[k] && parts[k] && (values[i] = pickOption(names[i], parts[k])) != null) used[k] = true;
      }
      var value = values[i];
      out.subtypes.push({ row: pos, options: names[i], value: value, auto: names[i].length === 1,
        choice: value == null ? null : row.sub[pos][names[i].indexOf(value)] });
      if (value == null && !out.missing) out.missing = { kind: 'seat', name: pos, row: pos, key: 'seat' };
    });
  }

  // "{year} {Make} {Model}[ {detail}] {step values}" (2016 Chevrolet Silverado 3500HD Crew Cab).
  // A part already said is skipped: Tesla's Model step "Model 3" must not read "Model 3 Model 3". A step that picks
  // the product (the mats' Floor Mat Set) or answers "N/A" says nothing about the vehicle, so it stays out too.
  function vehicleLabel(ctx, row) {
    if (!ctx || !ctx.make || !ctx.model) return '';
    var parts = [ctx.year, ctx.make.n, ctx.model.n].filter(Boolean).map(String);
    var seen = [slugify(ctx.model.n), ctx.model.s];
    var product = function (p) { return SF.isOptionStep && SF.isOptionStep(ctx, p[0]); };
    var extra = ((row && row.st) || []).map(function (p) { return p && !product(p) ? p[1] : null; });
    [row && row.d].concat(extra).forEach(function (v) {
      var s = slugify(v);
      if (!s || s === 'n-a' || seen.indexOf(s) >= 0) return;
      seen.push(s);
      parts.push(String(v).trim());
    });
    return parts.join(' ');
  }

  // Line-item properties: `Vehicle` shows in cart and checkout; underscore fields are hidden but reach the order.
  function cartProperties(ctx, row, picks) {
    var sel = stepsFor(ctx, picks);
    row = row || sel.row;
    if (!ctx || ctx.status !== 'ok' || !row) return {};
    var props = { Vehicle: vehicleLabel(ctx, row), _year: String(ctx.year), _make: ctx.make.n,
      _model: ctx.model.n, _gen: row.g, _fit_key: row.k };
    (row.st || []).forEach(function (p) {
      var name = slugify(p && p[0]);
      if (!name) return;
      var key = STEP_PROPS[name] || '_' + name.replace(/-/g, '_');
      while (key in props) key += '_2'; // two steps mapping to one field must both reach the order
      props[key] = p[1];
    });
    var subs = sel.subtypes.filter(function (s) { return s.value; });
    if (subs.length === 1) props._seat_subtype = subs[0].value;
    if (subs.length > 1) props._seat_subtype = subs.map(function (s) { return s.row + ': ' + s.value; }).join('; ');
    if (row.d) props._model_detail = row.d;
    Object.keys(props).forEach(function (k) { if (props[k] == null || props[k] === '') delete props[k]; });
    return props;
  }

  Object.assign(SF, { SUB_ROWS: SUB_ROWS, stepsFor: stepsFor, vehicleLabel: vehicleLabel,
    cartProperties: cartProperties });
  // Scripts that might run before these files can wait for this instead of racing them.
  SF.emit('ready', { SeatFit: SF });
})();
